import React, { useState, useEffect, useCallback } from 'react';
import { professionsApi } from '../../services/smartBillApi';
import './ProfessionsManagement.css';

const ProfessionsManagement = () => {
  const [professions, setProfessions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('active');
  const [showModal, setShowModal] = useState(false);
  const [editingProfession, setEditingProfession] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    is_active: true,
  });

  const loadProfessions = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await professionsApi.getAll(searchTerm, statusFilter);
      if (response.success) {
        setProfessions(response.professions || []);
      } else {
        setError(response.message || 'Failed to load professions');
      }
    } catch (err) {
      setError(err.message || 'Failed to load professions');
      console.error('Error loading professions:', err);
    } finally {
      setLoading(false);
    }
  }, [searchTerm, statusFilter]);

  useEffect(() => {
    loadProfessions();
  }, [loadProfessions]);

  const handleOpenModal = (profession = null) => {
    if (profession) {
      setEditingProfession(profession);
      setFormData({
        name: profession.name || '',
        description: profession.description || '',
        is_active: profession.is_active !== undefined ? profession.is_active : true,
      });
    } else {
      setEditingProfession(null);
      setFormData({
        name: '',
        description: '',
        is_active: true,
      });
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingProfession(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      if (editingProfession) {
        await professionsApi.update(editingProfession.id, formData);
      } else {
        await professionsApi.create(formData);
      }

      await loadProfessions();
      handleCloseModal();
    } catch (err) {
      setError(err.message || 'Failed to save profession');
      console.error('Error saving profession:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id, hardDelete = false) => {
    if (!window.confirm(`Are you sure you want to ${hardDelete ? 'permanently delete' : 'deactivate'} this profession?`)) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await professionsApi.delete(id, hardDelete);
      await loadProfessions();
    } catch (err) {
      setError(err.message || 'Failed to delete profession');
      console.error('Error deleting profession:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  return (
    <div className="professions-management">
      <div className="page-header">
        <h1>Professions Management</h1>
        <button className="btn btn-primary" onClick={() => handleOpenModal()}>
          <i className="fas fa-plus"></i> Add New Profession
        </button>
      </div>

      {error && (
        <div className="alert alert-error">
          <i className="fas fa-exclamation-circle"></i> {error}
        </div>
      )}

      <div className="filters-section">
        <div className="search-box">
          <input
            type="text"
            placeholder="Search professions..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
          <i className="fas fa-search"></i>
        </div>

        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="filter-select"
        >
          <option value="all">All Status</option>
          <option value="active">Active Only</option>
          <option value="false">Inactive Only</option>
        </select>
      </div>

      {loading && professions.length === 0 ? (
        <div className="loading">Loading professions...</div>
      ) : (
        <div className="table-container">
          <table className="professions-table">
            <thead>
              <tr>
                <th>Profession Name</th>
                <th>Description</th>
                <th>Status</th>
                <th>Created At</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {professions.length === 0 ? (
                <tr>
                  <td colSpan="5" className="no-data">
                    No professions found
                  </td>
                </tr>
              ) : (
                professions.map((profession) => (
                  <tr
                    key={profession.id}
                    className={!profession.is_active ? 'inactive' : ''}
                  >
                    <td>
                      <strong>{profession.name}</strong>
                    </td>
                    <td className="description-cell">
                      {profession.description || '-'}
                    </td>
                    <td>
                      <span
                        className={`badge ${
                          profession.is_active
                            ? 'badge-success'
                            : 'badge-danger'
                        }`}
                      >
                        {profession.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td>
                      {profession.created_at
                        ? new Date(profession.created_at).toLocaleDateString()
                        : '-'}
                    </td>
                    <td>
                      <div className="action-buttons">
                        <button
                          className="btn-icon btn-edit"
                          onClick={() => handleOpenModal(profession)}
                          title="Edit"
                        >
                          <i className="fas fa-edit"></i>
                        </button>
                        {profession.is_active ? (
                          <button
                            className="btn-icon btn-deactivate"
                            onClick={() => handleDelete(profession.id, false)}
                            title="Deactivate"
                          >
                            <i className="fas fa-eye-slash"></i>
                          </button>
                        ) : (
                          <>
                            <button
                              className="btn-icon btn-activate"
                              onClick={() =>
                                handleOpenModal({
                                  ...profession,
                                  is_active: true,
                                })
                              }
                              title="Activate"
                            >
                              <i className="fas fa-eye"></i>
                            </button>
                            <button
                              className="btn-icon btn-delete"
                              onClick={() => handleDelete(profession.id, true)}
                              title="Delete Permanently"
                            >
                              <i className="fas fa-trash"></i>
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Add/Edit Modal */}
      {showModal && (
        <div
          className="modal-overlay"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) {
              handleCloseModal();
            }
          }}
        >
          <div
            className="modal-content"
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <h2>
                {editingProfession
                  ? 'Edit Profession'
                  : 'Add New Profession'}
              </h2>
              <button className="modal-close" onClick={handleCloseModal}>
                <i className="fas fa-times"></i>
              </button>
            </div>

            <form onSubmit={handleSubmit} className="profession-form">
              <div className="form-row">
                <div className="form-group">
                  <label>Profession Name *</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    placeholder="e.g., Software Engineer"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Description (Optional)</label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    rows="3"
                    placeholder="Add a description for this profession..."
                    style={{
                      padding: '10px',
                      border: '1px solid #ddd',
                      borderRadius: '6px',
                      fontSize: '14px',
                      fontFamily: 'inherit',
                      resize: 'vertical',
                    }}
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group checkbox-group">
                  <label>
                    <input
                      type="checkbox"
                      name="is_active"
                      checked={formData.is_active}
                      onChange={handleInputChange}
                    />
                    Active
                  </label>
                </div>
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={handleCloseModal}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={loading}
                >
                  {loading
                    ? 'Saving...'
                    : editingProfession
                    ? 'Update'
                    : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProfessionsManagement;























